import styled, { css } from 'styled-components';
import { ReactNode } from 'react';
import { Link } from 'react-router-dom';

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'outline';
type ButtonSize = 'sm' | 'md' | 'lg';

interface CommonButtonProps {
  variant?: ButtonVariant;
  size?: ButtonSize;
  fullWidth?: boolean;
  children: ReactNode;
  icon?: ReactNode;
  iconPosition?: 'left' | 'right';
  className?: string;
}

interface ButtonElementProps extends CommonButtonProps {
  as?: 'button';
  type?: 'button' | 'submit' | 'reset';
  disabled?: boolean;
  onClick?: () => void;
}

interface AnchorElementProps extends CommonButtonProps {
  as: 'a';
  href: string;
  target?: string;
  rel?: string;
}

interface LinkElementProps extends CommonButtonProps {
  as: typeof Link;
  to: string;
}

type ButtonProps = ButtonElementProps | AnchorElementProps | LinkElementProps;

const sizeStyles = {
  sm: css`
    padding: var(--space-2) var(--space-4);
    font-size: var(--text-sm);
    border-radius: var(--radius-md);
    gap: var(--space-2);
  `,
  md: css`
    padding: var(--space-3) var(--space-6);
    font-size: var(--text-base);
    border-radius: var(--radius-lg);
    gap: var(--space-2);
  `,
  lg: css`
    padding: var(--space-4) var(--space-8);
    font-size: var(--text-lg);
    border-radius: var(--radius-lg);
    gap: var(--space-3);
  `,
};

const variantStyles = {
  primary: css`
    background: linear-gradient(135deg, var(--color-brand-primary), var(--color-brand-secondary));
    color: white;
    border: none;
    box-shadow: 0 4px 14px rgba(81, 20, 97, 0.25);

    &:hover:not(:disabled) {
      box-shadow: 0 6px 20px rgba(81, 20, 97, 0.35);
      transform: translateY(-2px);
    }

    &:active:not(:disabled) {
      transform: translateY(0);
    }
  `,
  secondary: css`
    background: var(--glass-background);
    backdrop-filter: var(--glass-blur);
    -webkit-backdrop-filter: var(--glass-blur);
    color: var(--color-text-primary);
    border: 1px solid var(--glass-border);
    box-shadow: var(--shadow-sm);

    &:hover:not(:disabled) {
      background: var(--glass-background-elevated);
      box-shadow: var(--shadow-md);
      transform: translateY(-2px);
    }

    &:active:not(:disabled) {
      transform: translateY(0);
    }
  `,
  ghost: css`
    background: transparent;
    color: var(--color-text-primary);
    border: none;

    &:hover:not(:disabled) {
      background: var(--color-border);
    }
  `,
  outline: css`
    background: transparent;
    color: var(--color-brand-primary);
    border: 2px solid var(--color-brand-primary);

    &:hover:not(:disabled) {
      background: var(--color-brand-primary);
      color: white;
    }
  `,
};

const buttonStyles = css<{
  $variant: ButtonVariant;
  $size: ButtonSize;
  $fullWidth: boolean;
  $iconPosition: 'left' | 'right';
}>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-family: var(--font-sans);
  font-weight: var(--font-semibold);
  text-decoration: none;
  cursor: pointer;
  transition: all var(--transition-base);
  white-space: nowrap;

  ${props => sizeStyles[props.$size]}
  ${props => variantStyles[props.$variant]}

  ${props => props.$fullWidth && css`
    width: 100%;
  `}

  ${props => props.$iconPosition === 'right' && css`
    flex-direction: row-reverse;
  `}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:focus-visible {
    outline: 2px solid var(--color-brand-primary);
    outline-offset: 2px;
  }

  svg {
    width: 1.2em;
    height: 1.2em;
  }
`;

const StyledButton = styled.button<{
  $variant: ButtonVariant;
  $size: ButtonSize;
  $fullWidth: boolean;
  $iconPosition: 'left' | 'right';
}>`
  ${buttonStyles}
`;

const StyledAnchor = styled.a<{
  $variant: ButtonVariant;
  $size: ButtonSize;
  $fullWidth: boolean;
  $iconPosition: 'left' | 'right';
}>`
  ${buttonStyles}
`;

const StyledLink = styled(Link)<{
  $variant: ButtonVariant;
  $size: ButtonSize;
  $fullWidth: boolean;
  $iconPosition: 'left' | 'right';
}>`
  ${buttonStyles}
`;

export function Button(props: ButtonProps) {
  const {
    variant = 'primary',
    size = 'md',
    fullWidth = false,
    children,
    icon,
    iconPosition = 'left',
    className,
  } = props;

  const styleProps = {
    $variant: variant,
    $size: size,
    $fullWidth: fullWidth,
    $iconPosition: iconPosition,
    className,
  };

  const content = (
    <>
      {icon && icon}
      {children}
    </>
  );

  if (props.as === 'a') {
    return (
      <StyledAnchor
        {...styleProps}
        href={props.href}
        target={props.target}
        rel={props.rel}
      >
        {content}
      </StyledAnchor>
    );
  }

  if (props.as === Link) {
    return (
      <StyledLink {...styleProps} to={props.to}>
        {content}
      </StyledLink>
    );
  }

  // Default button element
  const buttonProps = props as ButtonElementProps;
  return (
    <StyledButton
      {...styleProps}
      type={buttonProps.type}
      disabled={buttonProps.disabled}
      onClick={buttonProps.onClick}
    >
      {content}
    </StyledButton>
  );
}
